<template>
  <LazySection
    :is-height-fixed="!isFetched"
    :placeholder-height="500"
  >
    <seo-experience-block
      :text="seoText"
      :faq="seoFaq"
      :slug="props.slug"
      :current-tag="currentTag"
      :cityInfoNameRu="cityInfoNameRu"
      :slug-info="slugInfo"
      :default-sogu-fields="props.defaultSoguFields"
      :is-guides-link-shown="isGuidesLinkShown"
      :is-attraction-link-shown="isAttractionLinkShown"
      :is-reviews-link-shown="isReviewsLinkShown"
      :tags="hiddenTags"
      :route="route"
    />
  </LazySection>
</template>

<script setup lang="ts">
  import { SeoExperienceBlock } from '@experience/travelers-seo-block-package';
  import {
    buildExcursionAggregationTypeWithoutTours,
    excursionFiltersToQuery,
    useExcursionFiltersStore,
  } from '@/entities/filter/store/excursion';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { useCitySeoInfo } from '@/entities/city/api/useCitySeoInfo.ts';
  import { computed } from 'vue';
  import { useCityInfo } from '@/entities/city/api/useCityInfo.ts';
  import { useCityTag } from '@/entities/tag/api/useCityTag.ts';
  import type { CityInfo, CityTag } from '@/shared/api/experience';
  import { useGuidesList } from '@/widgets/guidesBlock/api/useGuidesList.ts';
  import { GUIDES_AMOUNT, REVIEWS_AMOUNT } from '@/widgets/guidesBlock/lib/consts.ts';
  import { useReviewList } from '@/entities/review';
  import {
    type ExcursionParams,
    useExcursionExperienceAggregations,
  } from '@/entities/experience/aggregation/api/excursion/useExcursionExperienceAggregations.ts';
  import { useSchemaFaq } from '@/widgets/seoBlock/composable/useSchemaFaq.ts';
  import { useCityTagsCollectionsExperiment } from '@/features/experiments';
  import { LazySection } from '@/shared/ui/LazySections';

  interface Props {
    slug: string;
    defaultSoguFields: ContextGeoSubject;
    page: number;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const filterStore = useExcursionFiltersStore();

  const currentTag = computed(() => {
    const slug = filterStore.searchState.tag__slugs;

    return slug && slug !== 'all' ? slug : 'all';
  });

  const { citySeoData, isFetched } = useCitySeoInfo(
    computed(() => props.slug),
    computed(() => currentTag.value || 'all'),
    computed(() => props.page),
    computed(() => true),
  );

  const seoText = computed(() => citySeoData.value?.seo?.text || '');
  const seoFaq = computed(() => citySeoData.value?.faq || {});

  const { cityInfo } = useCityInfo(
    computed(() => props.slug),
    computed(() => true),
  );

  const slugInfo = computed(() => cityInfo.value || ({} as CityInfo));

  const cityInfoNameRu = computed(() => cityInfo.value?.name_ru || '');

  const { isExpActive } = useCityTagsCollectionsExperiment();
  const { cityTags } = useCityTag({
    slug: computed(() => props.slug),
    enabled: computed(() => true),
    sorting: isExpActive.value ? 'ranking_mab' : '-popularity',
  });

  const attractionTags = computed(() => {
    return cityTags.value?.reduce((acc, tag) => {
      if (tag.flags?.is_attraction && !tag.flags?.is_seo_only && tag.images?.cover) {
        acc.push(tag);
      }

      return acc;
    }, [] as CityTag[]);
  });

  const isAttractionLinkShown = computed(() => {
    return Boolean(attractionTags.value?.length);
  });

  const { guidesList } = useGuidesList({
    city__slug: computed(() => props.slug),
    page_size: computed(() => GUIDES_AMOUNT),
    sorting: computed(() => '-review_rating'),
    guide_types: 'guide',
  });

  const { reviewsList } = useReviewList({
    city__slug: computed(() => props.slug),
    is_long: computed(() => 1),
    limit: computed(() => REVIEWS_AMOUNT),
    rate_max: computed(() => 5),
    rate_min: computed(() => 4),
    tag__slug: 'all',
  });

  const isReviewsLinkShown = computed(() => {
    return Boolean(reviewsList.value?.length);
  });

  const isGuidesLinkShown = computed(() => {
    return Boolean(guidesList.value?.results?.length);
  });

  const aggregationParams = computed<ExcursionParams>((): ExcursionParams => {
    const pageParams = {
      city: props.slug,
    };

    return {
      date_filter_mode: 'event_start',
      bookable: true,
      start_time_interval: 'day',
      duration_interval: 'upto_2_hours,from_6_hours,from_2_to_4_hours',
      ...excursionFiltersToQuery(filterStore.searchState, pageParams),
      type: buildExcursionAggregationTypeWithoutTours(filterStore.searchState),
      page: computed(() => 1),
    } as ExcursionParams;
  });

  const { tags } = useExcursionExperienceAggregations(aggregationParams);

  const modifyTagsWithAggregation = (arr: CityTag[]) => {
    return arr
      .map((item) => {
        const id = item?.id || 0;

        return { ...item, counter: tags.value?.[id] || 0 };
      })
      .filter((tag) => !!tag?.counter);
  };

  const hiddenTags = computed(() => {
    const arr =
      cityTags.value?.reduce((acc, tag) => {
        if (
          (tag.flags?.is_hidden || tag.flags?.is_seo_only) &&
          !tag.flags?.is_attraction &&
          (tag.counters?.excursions_count ?? 0) > 0
        ) {
          acc.push(tag);
        }

        return acc;
      }, [] as CityTag[]) || [];

    const modifiedTags = modifyTagsWithAggregation(arr);

    return isExpActive.value ? modifiedTags.slice(10, 30) : modifiedTags;
  });

  useSchemaFaq(seoFaq);
</script>

<style lang="scss" scoped>
  :deep(.seo-block__inner li) {
    list-style: disc;
  }
</style>
